/**
 * Services page content.
 *
 * Every string in this file is transcribed verbatim from the approved
 * "Services Page (SEO Optimized 2026)" requirement document. Do not reword,
 * shorten or extend this copy — update the document first.
 *
 * `logoKeys` reference `src/data/certificationLogos.js`; standards the project
 * holds no logo file for carry an empty list rather than a stand-in graphic.
 */

export const heroContent = {
  h1: "Our Professional Certification & Compliance Services",
  subtitle: "Helping Businesses Achieve Global Standards With Confidence",
  paragraphs: [
    "MBA Solutions provides professional consultancy services that help organizations implement international standards, improve operational performance and achieve certification goals.",
    "Our experienced consultants support businesses through complete certification journeys — from initial assessment and documentation to implementation, training and audit preparation.",
    "We deliver practical solutions designed around your business needs, industry requirements and global market expectations.",
  ],
};

/* `to` / `bulletsTo` are routing only (2026-09-28): each standard card opens
   its own existing page where one exists, otherwise its area's service page. */
export const serviceAreasTitle = "Our Service Areas";

export const serviceAreas = [
  {
    id: "iso-certification-consultancy",
    icon: "certificate",
    title: "1. ISO Certification Consultancy",
    /* `readMore` renders the "Read Full Explanation" link beside the heading,
       for those areas that have a dedicated page of their own */
    readMore: { label: "Read Full Explanation", to: "/iso-certification-services" },
    intro:
      "MBA Solutions helps organizations implement internationally recognized ISO management systems to improve quality, safety, efficiency and business performance.",
    listLabel: "Our ISO consultancy services include:",
    items: [
      {
        title: "ISO 9001 – Quality Management System",
        to: "/iso-lead-auditor-training/iso-9001",
        description:
          "We help organizations establish effective quality management systems that improve customer satisfaction, process control and continuous improvement.",
        benefitsLabel: "Benefits:",
        benefits: [
          "Improved operational efficiency",
          "Better customer confidence",
          "Standardized business processes",
          "Continuous improvement culture",
        ],
        logoKeys: ["iso-9001"],
      },
      {
        title: "ISO 14001 – Environmental Management System",
        to: "/iso-certification-services",
        description:
          "Our consultancy helps businesses reduce environmental impact and develop responsible environmental practices.",
        benefitsLabel: "Benefits:",
        benefits: [
          "Better environmental performance",
          "Resource efficiency improvement",
          "Sustainability compliance",
          "Reduced environmental risks",
        ],
        logoKeys: ["iso-14001"],
      },
      {
        title: "ISO 45001 – Occupational Health & Safety Management System",
        to: "/iso-lead-auditor-training/iso-45001",
        description:
          "We support organizations in creating safer workplaces through effective occupational health and safety systems.",
        benefitsLabel: "Benefits:",
        benefits: [
          "Reduced workplace risks",
          "Improved employee safety",
          "Better compliance",
          "Safer operations",
        ],
        logoKeys: ["iso-45001"],
      },
      {
        title: "ISO 22000 – Food Safety Management System",
        to: "/iso-lead-auditor-training/iso-22000",
        description:
          "We help food businesses develop food safety systems that control risks and meet international requirements.",
        benefitsLabel: "Benefits:",
        benefits: [
          "Improved food safety controls",
          "Buyer confidence",
          "Better regulatory compliance",
          "Market access opportunities",
        ],
        logoKeys: ["iso-22000"],
      },
      {
        title: "ISO 27001 – Information Security Management System",
        to: "/iso-lead-auditor-training/iso-27001",
        description:
          "Our consultancy supports organizations in protecting valuable information assets through effective security management systems.",
        benefitsLabel: "Benefits:",
        benefits: [
          "Improved data protection",
          "Risk management",
          "Customer trust",
          "Information security improvement",
        ],
        logoKeys: ["iso-27001"],
      },
      {
        title: "ISO 50001 – Energy Management System",
        to: "/iso-lead-auditor-training/iso-50001",
        description:
          "We help organizations improve energy efficiency and reduce unnecessary energy consumption.",
        benefitsLabel: "Benefits:",
        benefits: [
          "Reduced energy costs",
          "Better resource management",
          "Sustainability improvement",
        ],
        logoKeys: ["iso-50001"],
      },
    ],
  },
  {
    id: "food-safety-compliance-services",
    icon: "food",
    title: "2. Food Safety & Compliance Services",
    readMore: { label: "Read Full Explanation", to: "/food-safety-services" },
    intro:
      "MBA Solutions supports food manufacturers and related businesses in achieving international food safety standards.",
    listLabel: null,
    items: [
      {
        title: "HACCP Certification",
        to: "/iso-lead-auditor-training/haccp",
        description:
          "We help organizations identify food safety hazards and implement preventive control systems.",
        logoKeys: ["haccp"],
      },
      {
        title: "Halal Certification",
        to: "/food-safety-services",
        description:
          "Our consultancy helps businesses meet halal compliance requirements for local and international markets.",
        logoKeys: ["halal"],
      },
      {
        title: "FSSC 22000",
        to: "/food-safety-services",
        description:
          "We support organizations in implementing globally recognized food safety management systems.",
        logoKeys: ["fssc-22000"],
      },
      {
        title: "BRCGS Food Safety",
        to: "/iso-lead-auditor-training/brcgs",
        description:
          "Helping suppliers meet international retailer and food industry requirements.",
        logoKeys: ["brcgs"],
      },
      {
        title: "GMP & GHP Implementation",
        to: "/iso-lead-auditor-training/gmp",
        description:
          "Supporting businesses in maintaining good manufacturing and hygiene practices.",
        logoKeys: ["gmp"],
      },
    ],
  },
  {
    id: "sustainability-esg-consulting",
    icon: "sustainability",
    title: "3. Sustainability & ESG Consulting",
    readMore: { label: "Read Full Explanation", to: "/sustainability-esg-services" },
    intro:
      "Modern businesses require strong sustainability practices to meet buyer, investor and regulatory expectations.",
    listLabel: "MBA Solutions provides:",
    items: [
      {
        title: "EcoVadis Consulting",
        to: "/iso-lead-auditor-training/ecovadis-support",
        description:
          "Support for improving sustainability ratings and preparing organizations for global buyer assessments.",
        logoKeys: ["ecovadis"],
      },
      {
        title: "ESG Consulting",
        to: "/sustainability-esg-services",
        description:
          "Helping companies improve Environmental, Social and Governance performance.",
        logoKeys: ["esg"],
      },
      {
        title: "GRI Reporting Support",
        to: "/sustainability-esg-services",
        description:
          "Guidance for sustainability reporting according to global frameworks.",
        logoKeys: ["gri"],
      },
      {
        title: "SA8000 Compliance",
        to: "/sustainability-esg-services",
        description:
          "Supporting ethical workplace practices and social accountability requirements.",
        logoKeys: ["sa8000"],
      },
    ],
  },
  {
    id: "textile-responsible-manufacturing-standards",
    icon: "textile",
    title: "4. Textile & Responsible Manufacturing Standards",
    readMore: { label: "Read Full Explanation", to: "/textile-compliance-services" },
    intro:
      "MBA Solutions supports textile manufacturers, exporters and supply chain partners with international compliance requirements.",
    listLabel: "Our services include:",
    items: [
      {
        title: "GOTS Certification",
        to: "/textile-compliance-services",
        description:
          "Supporting organic textile production and responsible processing requirements.",
        logoKeys: ["gots"],
      },
      {
        title: "GRS Certification",
        to: "/textile-compliance-services",
        description:
          "Helping businesses verify recycled materials and responsible production practices.",
        logoKeys: ["grs"],
      },
      {
        title: "RCS & OCS Certification",
        to: "/textile-compliance-services",
        description:
          "Supporting organizations with recycled and organic content verification.",
        logoKeys: ["rcs-ocs"],
      },
      {
        title: "WRAP Certification",
        to: "/iso-lead-auditor-training/wrap",
        description:
          "Helping manufacturers demonstrate ethical and responsible production.",
        logoKeys: ["wrap"],
      },
      {
        title: "Sedex / SMETA",
        to: "/iso-lead-auditor-training/sedex-semeta",
        description:
          "Supporting responsible sourcing and social compliance requirements.",
        logoKeys: ["sedex-smeta"],
      },
    ],
  },
  {
    id: "supply-chain-certification-services",
    icon: "supplyChain",
    title: "5. Supply Chain Certification Services",
    readMore: { label: "Read Full Explanation", to: "/supply-chain-certification-services" },
    intro: null,
    listLabel: null,
    items: [
      {
        title: "FSC Chain of Custody (FSC CoC)",
        to: "/supply-chain-certification-services",
        description:
          "Helping organizations prove responsible sourcing of forest-based materials including paper, packaging and textile-related materials.",
        logoKeys: ["fsc-coc"],
      },
      {
        title: "PEFC Certification",
        to: "/supply-chain-certification-services",
        description:
          "Supporting sustainable forestry and responsible supply chain practices.",
        logoKeys: ["pefc"],
      },
    ],
  },
  {
    id: "training-implementation-support",
    icon: "training",
    title: "6. Training & Implementation Support",
    intro:
      "MBA Solutions provides professional training programs to help teams understand and maintain management systems.",
    listLabel: "Training Areas:",
    bulletsTo: "/training",
    bullets: [
      "ISO Awareness Training",
      "Internal Auditor Training",
      "Food Safety Training",
      "Compliance Training",
      "Sustainability Awareness",
    ],
  },
  {
    id: "gap-assessment-audit-support",
    icon: "gap",
    title: "7. Gap Assessment & Audit Support",
    intro:
      "Our consultants evaluate your current systems against required standards and provide clear improvement recommendations.",
    listLabel: "Our process includes:",
    bulletsTo: "/iso-lead-auditor-training/gap-analysis",
    bullets: [
      "Current system review",
      "Documentation evaluation",
      "Process assessment",
      "Risk identification",
      "Improvement roadmap",
    ],
  },
];

export const whyChooseContent = {
  title: "Why Choose MBA Solutions?",
  // Rendered with a "✔" marker so the line reads exactly as written in the
  // document, while keeping the glyph out of the accessible name.
  items: [
    "Experienced Certification Consultants",
    "Industry-Based Solutions",
    "Complete Implementation Support",
    "International Standards Expertise",
    "Practical Business Approach",
  ],
};

export const finalCtaContent = {
  title: "Ready to Achieve Your Certification Goals?",
  paragraph:
    "Partner with MBA Solutions for expert consultancy, compliance solutions and sustainable business improvement.",
  cta: { label: "Request Your Consultation Today", to: "/contact" },
};
